import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FiArrowRight, FiCheckCircle, FiPhone, FiMail } from 'react-icons/fi';
import YouTube from 'react-youtube';
import HeroSlider from '../components/HeroSlider';
import '../styles/Home.css';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const { scrollY: scrollProgress } = useScroll();

  useEffect(() => {
    return scrollProgress.onChange((latest) => {
      setScrollY(latest);
    });
  }, [scrollProgress]);

  // Parallax effect
  const y1 = useTransform(scrollProgress, [0, 300], [0, -50]);
  const y2 = useTransform(scrollProgress, [300, 600], [0, -80]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  return (
    <div className="home-page">
      {/* Hero Slider - 4 Auto-Rotating Image Slides */}
      <HeroSlider />

      {/* Statistics Section */}
      <motion.section
        className="stats-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <motion.h2 variants={itemVariants} className="section-title">
            Why Choose Active Care Low
          </motion.h2>

          <div className="stats-grid">
            {[
              { number: '18-65', label: 'Age Group Served' },
              { number: '24/7', label: 'Professional Support' },
              { number: '£2.5K-5K', label: 'Weekly Fees (Local Authority)' },
              { number: 'UK', label: 'Regulatory Compliance' }
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="stat-card"
                variants={itemVariants}
                whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}
              >
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Features Section with Image Placement 1 */}
      <motion.section
        className="features-section"
        style={{ y: y1 }}
      >
        <div className="container">
          <div className="features-grid">
            <motion.div
              className="features-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Our Core Services</h2>

              {[
                { icon: FiCheckCircle, title: 'Person-Centered Care', desc: 'High quality care promoting emotional well-being and independence' },
                { icon: FiCheckCircle, title: 'Professional Support', desc: 'Access to therapists and mental health services 24/7' },
                { icon: FiCheckCircle, title: 'Individualized Plans', desc: 'Customized care plans for each resident' },
                { icon: FiCheckCircle, title: 'Trained Staff', desc: 'Safeguarding and trauma-informed care expertise' }
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  className="feature-item"
                  variants={itemVariants}
                  whileHover={{ x: 10 }}
                >
                  <feature.icon className="feature-icon" />
                  <div>
                    <h3>{feature.title}</h3>
                    <p>{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <div className="features-image">
              {/* IMAGE PLACEMENT 1: Place your image here */}
              <img src="/images/acr15.jpg" alt="Active Care Services" />
              {/* <div className="image-placeholder" style={{ backgroundColor: '#ecf0f1' }}>
              </div> */}
            </div>
          </div>
        </div>
      </motion.section>

      {/* YouTube Video Section */}
      <motion.section
        className="video-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Learn More About Our Services</h2>
          <p className="section-subtitle">Watch our introduction video</p>

          <div className="youtube-container">
            <div className="youtube-placeholder">
              <YouTube 
                  videoId="OPNdDSyd9AM" 
                  opts={{ 
                    width: '100%',
                    height: '100%',
                    playerVars: {
                      autoplay: 0,
                      controls: 1,
                      modestbranding: 1,
                      rel: 0,
                      fs: 1,
                    }
                  }}
                  onReady={() => console.log('YouTube player ready')}
                />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Mission Statement Section with Image Placement 2 */}
      <motion.section
        className="mission-section"
        style={{ y: y2 }}
      >
        <div className="container">
          <div className="mission-grid">
            <div className="mission-image">
              {/* IMAGE PLACEMENT 2: Place your mission image here */}
              <img src="/images/care-home.jpg" alt="Our Mission" />
            </div>

            <motion.div
              className="mission-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Our Mission</h2>
              <p className="section-text">
                Our mission statement is to empower vulnerable adults to reach their full potentials by providing a stable and supportive environment where they can feel safe and supported regardless of background, disabilities and sex, origin.
              </p>

              <div className="mission-highlights">
                {[
                  'High life stability for residents',
                  'Positive therapeutic outcomes',
                  'Education and training opportunities',
                  'Employment pathway support'
                ].map((highlight, i) => (
                  <motion.div
                    key={i}
                    className="mission-highlight"
                    variants={itemVariants}
                  >
                    <FiCheckCircle />
                    <span>{highlight}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Partners Section with Image Placement 3 */}
      <motion.section
        className="partners-preview-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Our Strategic Partnerships</h2>

          <div className="partners-grid">
            {[
              { name: 'Local Authorities', desc: 'Primary commissioning partners' },
              { name: 'Healthcare Services', desc: 'Hospital and A/E referrals' },
              { name: 'Social Services', desc: 'Coordinated care delivery' },
              { name: 'Mental Health Providers', desc: 'Therapeutic support' },
              { name: 'Care Agencies', desc: 'Quality oversight' },
              { name: 'Community Resources', desc: 'Integration support' }
            ].map((partner, i) => (
              <motion.div
                key={i}
                className="partner-card"
                variants={itemVariants}
                whileHover={{ y: -10, boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)' }}
              >
                <h3>{partner.name}</h3>
                <p>{partner.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="partners-image">
            {/* IMAGE PLACEMENT 3: Place your partnership image here */}
            <img src="/images/acr13.jpg" alt="Our Partners" />
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2>Ready to Make a Referral?</h2>
          <p>Contact us today to discuss how we can support your needs</p>

          <div className="cta-buttons">
            <motion.button
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiPhone /> Call Us
            </motion.button>
            <motion.button
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiMail /> Email Us
            </motion.button>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Home;
